import type { DashboardActivityItem } from '@/lib/types';

interface RecentActivityProps {
  items: DashboardActivityItem[];
  emptyMessage?: string;
}

function formatTimeAgo(dateString: string) {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';

  const now = new Date();
  const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

  if (diffInHours < 1) return 'Just now';
  if (diffInHours < 24) return `${diffInHours}h ago`;

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) return `${diffInDays}d ago`;

  return date.toLocaleDateString();
}

export default function RecentActivity({
  items,
  emptyMessage = 'No recent activity yet.',
}: RecentActivityProps) {
  return (
    <div className="rounded-xl border border-slate-100 bg-white p-5 shadow-sm h-full">
      <h3 className="text-base font-semibold text-slate-900">Recent activity</h3>
      {items.length === 0 ? (
        <p className="mt-6 text-sm text-slate-500">{emptyMessage}</p>
      ) : (
        <ul className="mt-4 divide-y divide-slate-100">
          {items.map((item, index) => (
            <li key={`${item.title}-${item.timestamp}-${index}`} className="flex items-start gap-3 py-3 first:pt-0 last:pb-0">
              <div className="mt-1 h-2 w-2 shrink-0 rounded-full bg-amber-400" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-slate-900">{item.title}</p>
                {item.subtitle && (
                  <p className="truncate text-sm text-slate-500">{item.subtitle}</p>
                )}
                {item.meta && (
                  <p className="mt-0.5 text-xs text-slate-400">{item.meta}</p>
                )}
              </div>
              <span className="shrink-0 text-xs text-slate-400">
                {formatTimeAgo(item.timestamp)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
